import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Plus, Loader2, Pencil, Trash2, Save, Zap, Info,
} from "lucide-react";
import EmailSignatureCard from "./EmailSignatureCard";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_META = {
  demo_requested: { label: "Demo Request", color: "#1D4ED8" },
  responded: { label: "General Enquiry", color: "#FFD108" },
  couldnt_reach: { label: "Couldn't Reach", color: "#6B5600" },
  custom: { label: "Custom", color: "#8A857A" },
};

const STATUS_META = {
  draft: { label: "Draft", color: "#8A6D00", bg: "#FFF8D6" },
  active: { label: "Live", color: "#0E6B3F", bg: "#E3F3E9" },
  retired: { label: "Retired", color: "#8A857A", bg: "#F4F2ED" },
};

const emptyTemplate = {
  name: "", enquiry_type: "custom", is_auto_ack: false,
  industry: "general", purpose: "cold_intro", products: [], region: ["AU"],
  subject_options: [""], body: "", link_url: "", addon_ids: [], status: "draft", owner: "",
};

const titleise = (s) => String(s || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export default function ProspectEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ default_from_name: "", default_from_email: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplate);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [meta, setMeta] = useState({ industries: [], purposes: [], products: [], regions: [], approved_links: [] });
  const [addons, setAddons] = useState([]);
  const [flags, setFlags] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes, aRes] = await Promise.all([
        axios.get(`${API}/sales/email-templates`),
        axios.get(`${API}/sales/settings`),
        axios.get(`${API}/sales/template-addons`),
      ]);
      setTemplates(tRes.data.templates || []);
      setMeta({
        industries: tRes.data.industries || [], purposes: tRes.data.purposes || [],
        products: tRes.data.products || [], regions: tRes.data.regions || [],
        approved_links: tRes.data.approved_links || [],
      });
      setAddons(aRes.data.addons || []);
      setSettings(sRes.data.settings || { default_from_name: "", default_from_email: "" });
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => { setEditing(null); setFlags([]); setForm(emptyTemplate); setFormOpen(true); };
  const openEdit = (t) => {
    setEditing(t.id); setFlags([]);
    setForm({
      name: t.name || "", enquiry_type: t.enquiry_type || "custom", is_auto_ack: !!t.is_auto_ack,
      industry: t.industry || "general", purpose: t.purpose || "cold_intro",
      products: t.products || [], region: t.region?.length ? t.region : ["AU"],
      subject_options: t.subject_options?.length ? t.subject_options : [t.subject || ""],
      body: t.body || "", link_url: t.link_url || "", addon_ids: t.addon_ids || [],
      status: t.status || "draft", owner: t.owner || "",
    });
    setFormOpen(true);
  };

  const saveTemplate = async () => {
    const subjects = form.subject_options.map((s) => s.trim()).filter(Boolean);
    if (!form.name.trim() || !subjects.length || !form.body.trim()) {
      toast.error("Name, at least one subject line and a body are required"); return;
    }
    setSaving(true); setFlags([]);
    try {
      const payload = { ...form, subject_options: subjects };
      const { data } = editing
        ? await axios.put(`${API}/sales/email-templates/${editing}`, payload)
        : await axios.post(`${API}/sales/email-templates`, payload);
      if (data.success === false) {
        setFlags(data.flags || []);
        toast.error(data.detail || "The house rules stopped this being saved");
        return;
      }
      setFlags((data.flags || []).filter((f) => f.severity !== "block"));
      toast.success(editing ? `Saved as version ${data.template?.version}` : "Template created");
      setFormOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    try {
      const { data } = await axios.put(`${API}/sales/email-templates/${t.id}`, { status });
      if (data.success === false) {
        toast.error(data.flags?.find((f) => f.severity === "block")?.message || data.detail);
        return;
      }
      toast.success(status === "active" ? `${t.name} is live and Claude can draft from it`
        : status === "retired" ? `${t.name} retired` : `${t.name} back to draft`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change that"); }
  };

  const removeTemplate = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/sales/email-templates/${id}`);
      toast.success("Template deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/sales/settings`, settings);
      toast.success("Sender settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-templates-view">
      <EmailSignatureCard />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#8A6D00]" />
          </div>
          <div>
            <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">Email Templates</h2>
            <p className="text-sm text-[#59554D]">Outreach templates for prospects — auto-sent on inbound leads and reusable for follow-ups</p>
          </div>
        </div>
        <Button onClick={openAdd} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium" data-testid="template-add-btn">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Token help */}
      <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 flex items-start gap-2 text-xs text-[#59554D]">
        <Info className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
        <div>
          Use merge tokens in the subject/body — they're filled per prospect when sent:
          <span className="text-[#59554D]"> {"{{first_name}}"}, {"{{contact_name}}"}, {"{{company_name}}"}, {"{{contact_email}}"}, {"{{rep_name}}"}, {"{{source}}"}</span>.
          <span className="block mt-1 text-[#8A857A]">Auto-acknowledgement templates send automatically the moment a matching inbound lead arrives, from the default sender below.</span>
        </div>
      </div>

      {/* Sender settings */}
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Default sender (auto-acknowledgements)</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs text-[#59554D]">From name</label>
              <Input value={settings.default_from_name || ""} onChange={(e) => setSettings({ ...settings, default_from_name: e.target.value })}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="settings-from-name" />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">From email</label>
              <Input value={settings.default_from_email || ""} onChange={(e) => setSettings({ ...settings, default_from_email: e.target.value })}
                placeholder="sales@equipr.software" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="settings-from-email" />
            </div>
            <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black font-medium" data-testid="settings-save">
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
          </div>
          <p className="text-[11px] text-[#8A857A]">Note: the sending domain must be verified in Resend or emails will be rejected.</p>
        </CardContent>
      </Card>

      {/* Templates list */}
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="p-10 text-center">
              <Mail className="w-8 h-8 mx-auto text-[#8A857A] mb-2" />
              <p className="text-sm text-[#59554D]">No templates yet</p>
              <p className="text-xs text-[#8A857A] mt-1">Click “New Template” to create your first outreach email.</p>
            </div>
          ) : (
            templates.map((t) => {
              const m = ENQUIRY_META[t.enquiry_type] || ENQUIRY_META.custom;
              const st = STATUS_META[t.status] || STATUS_META.draft;
              return (
                <div key={t.id} className="flex items-center gap-4 px-5 py-4 border-b border-[#F4F2ED] hover:bg-[#FFFFFF] transition-colors" data-testid={`template-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#1A1A1A] font-medium flex items-center gap-2 flex-wrap">
                      {t.name}
                      <Badge className="border-0 text-[10px]" style={{ background: st.bg, color: st.color }}
                        data-testid={`template-status-${t.id}`}>{st.label} · v{t.version}</Badge>
                      <Badge className="border-0 text-[10px]" style={{ background: `${m.color}1A`, color: m.color }}>{titleise(t.industry)} / {titleise(t.purpose)}</Badge>
                      {t.is_auto_ack && <Badge className="bg-[#E3F3E9] text-[#0E6B3F] border-0 text-[10px] gap-1"><Zap className="w-3 h-3" /> Auto-ack</Badge>}
                    </div>
                    <div className="text-xs text-[#8A857A] truncate mt-0.5">
                      {t.subject_options?.[0] || t.subject}
                      {t.subject_options?.length > 1 && ` (+${t.subject_options.length - 1} more)`}
                      {t.region?.length ? ` · ${t.region.join(", ")}` : ""}
                      {t.addon_ids?.length ? ` · add-ons: ${t.addon_ids.join(", ")}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.status !== "active" ? (
                      <Button size="sm" variant="outline" onClick={() => setStatus(t, "active")}
                        className="h-8 border-[#A9CFBB] bg-transparent text-[#0E6B3F] hover:bg-[#E3F3E9]"
                        data-testid={`template-activate-${t.id}`}>Make live</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(t, "retired")}
                        className="h-8 border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]"
                        data-testid={`template-retire-${t.id}`}>Retire</Button>
                    )}
                    <Button size="icon" variant="outline" title="Edit"
                      className="h-8 w-8 border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"
                      onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="outline" title="Delete"
                      className="h-8 w-8 border-[#DAD5CA] bg-transparent text-[#C23A00] hover:bg-[#FFE9E0] hover:text-[#C23A00]"
                      onClick={() => setConfirmDelete(t)} data-testid={`template-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {flags.length > 0 && (
              <div className="rounded-lg border p-2.5 space-y-1" data-testid="template-flags"
                style={{ background: "#FFF8D6", borderColor: "#E8C86A" }}>
                {flags.map((f, i) => (
                  <div key={i} className="text-[12.5px] text-[#6B5600]">
                    <strong>{f.severity === "block" ? "Blocked" : "Worth a look"}:</strong> {f.message}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Template name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-field-name" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Industry</label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-field-industry"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {meta.industries.map((s) => <SelectItem key={s} value={s}>{titleise(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Purpose</label>
                <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-field-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {meta.purposes.map((s) => <SelectItem key={s} value={s}>{titleise(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Subject lines * (two or three, so you can see which earns the open)</label>
              {form.subject_options.map((s, i) => (
                <Input key={i} value={s} data-testid={`template-field-subject-${i}`}
                  onChange={(e) => setForm({ ...form, subject_options: form.subject_options.map((x, j) => j === i ? e.target.value : x) })}
                  placeholder={i === 0 ? "Keeping your cranes compliant" : "Another angle"}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] mt-1" />
              ))}
              <button type="button" data-testid="template-add-subject"
                onClick={() => setForm({ ...form, subject_options: [...form.subject_options, ""] })}
                className="text-[11.5px] font-semibold text-[#8A6D00] hover:underline mt-1.5">Add another subject line</button>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Body * (plain text. Tokens in braces: {"{first_name}"}, {"{company}"}, {"{personal_line}"}, {"{addon}"}. The signature is added for you)</label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[200px] text-[13px]" data-testid="template-field-body" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Link (approved pages only)</label>
                <Select value={form.link_url || "__none__"} onValueChange={(v) => setForm({ ...form, link_url: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-field-link"><SelectValue placeholder="No link" /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectItem value="__none__">No link</SelectItem>
                    {meta.approved_links.map((u) => <SelectItem key={u} value={u}>{u.replace("https://www.", "")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Regions</label>
                <div className="flex gap-1.5 mt-1.5">
                  {meta.regions.map((r) => {
                    const on = form.region.includes(r);
                    return (
                      <button key={r} type="button" data-testid={`template-region-${r}`}
                        onClick={() => setForm({ ...form, region: on ? form.region.filter((x) => x !== r) : [...form.region, r] })}
                        className={`px-2.5 py-1 rounded-full text-[12px] border ${on ? "bg-[#1A1A1A] border-[#1A1A1A] text-white" : "bg-white border-[#DAD5CA] text-[#59554D]"}`}>{r}</button>
                    );
                  })}
                </div>
              </div>
            </div>
            {addons.length > 0 && (
              <div>
                <label className="text-xs text-[#59554D]">Add-on paragraphs this template may use</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {addons.map((a) => {
                    const on = form.addon_ids.includes(a.addon_id);
                    return (
                      <button key={a.addon_id} type="button" title={a.text} data-testid={`template-addon-${a.addon_id}`}
                        onClick={() => setForm({ ...form, addon_ids: on ? form.addon_ids.filter((x) => x !== a.addon_id) : [...form.addon_ids, a.addon_id] })}
                        className={`px-2.5 py-1 rounded-full text-[12px] border ${on ? "bg-[#1A1A1A] border-[#1A1A1A] text-white" : "bg-white border-[#DAD5CA] text-[#59554D]"}`}>
                        {a.addon_id}{a.valid_to ? ` (to ${a.valid_to})` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-[#59554D] cursor-pointer">
                <Switch checked={form.is_auto_ack} onCheckedChange={(v) => setForm({ ...form, is_auto_ack: v })} data-testid="template-field-autoack" />
                Auto-send on matching inbound lead
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium" data-testid="template-form-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} {editing ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !deleting) setConfirmDelete(null); }}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="template-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#59554D]">
              This removes <span className="text-[#1A1A1A] font-medium">{confirmDelete?.name}</span>. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid="template-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete && removeTemplate(confirmDelete.id); }}
              disabled={deleting} className="bg-[#C23A00] hover:bg-[#FFE9E0] text-[#1A1A1A]" data-testid="template-delete-confirm-btn">
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
